import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuthStore } from '../lib/auth';

const WS_RECONNECT_DELAY = 3000;
const WS_PING_INTERVAL = 25000;

export function useRealtimeMessages({ onNewMessage, onReadReceipt, onOnlineStatus, onTyping, onChatMessage, onChatTyping, onChatRead } = {}) {
  const { user, accessToken } = useAuthStore();
  const wsRef = useRef(null);
  const pingRef = useRef(null);
  const reconnectRef = useRef(null);
  const mountedRef = useRef(true);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [connected, setConnected] = useState(false);

  // Store callbacks in refs to avoid re-connecting on callback change
  const onNewMessageRef = useRef(onNewMessage);
  const onReadReceiptRef = useRef(onReadReceipt);
  const onOnlineStatusRef = useRef(onOnlineStatus);
  const onTypingRef = useRef(onTyping);
  const onChatMessageRef = useRef(onChatMessage);
  const onChatTypingRef = useRef(onChatTyping);
  const onChatReadRef = useRef(onChatRead);
  
  useEffect(() => { onNewMessageRef.current = onNewMessage; }, [onNewMessage]);
  useEffect(() => { onReadReceiptRef.current = onReadReceipt; }, [onReadReceipt]);
  useEffect(() => { onOnlineStatusRef.current = onOnlineStatus; }, [onOnlineStatus]);
  useEffect(() => { onTypingRef.current = onTyping; }, [onTyping]);
  useEffect(() => { onChatMessageRef.current = onChatMessage; }, [onChatMessage]);
  useEffect(() => { onChatTypingRef.current = onChatTyping; }, [onChatTyping]);
  useEffect(() => { onChatReadRef.current = onChatRead; }, [onChatRead]);

  const connect = useCallback(() => {
    if (!accessToken || !user?.id) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    // Build WS URL from REACT_APP_BACKEND_URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const host = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${host}/api/ws?token=${accessToken}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;
        setConnected(true);
        // Start ping interval to keep connection alive
        pingRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, WS_PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;
        try {
          const data = JSON.parse(event.data);
          switch (data.type) {
            case 'online_status':
              setOnlineUsers(data.online_users || []);
              onOnlineStatusRef.current?.(data.online_users || []);
              break;
            case 'new_message':
              onNewMessageRef.current?.(data.message);
              break;
            case 'read_receipt':
              onReadReceiptRef.current?.(data);
              break;
            case 'typing':
              onTypingRef.current?.(data);
              break;
            case 'chat_message':
              onChatMessageRef.current?.(data.message);
              break;
            case 'chat_typing':
              onChatTypingRef.current?.(data);
              break;
            case 'chat_read':
              onChatReadRef.current?.(data);
              break;
            case 'sticky_reminder':
              // Fan-out via a window event so the StickyNotesWidget (mounted
              // globally in the layout) can show an in-app toast without
              // requiring a prop wiring through every consumer of this hook.
              try {
                window.dispatchEvent(new CustomEvent('sticky-reminder', { detail: data }));
              } catch {}
              break;
            case 'mail:new':
              // Same fan-out pattern as sticky_reminder. The MailViewer
              // listens for this event and refreshes folder counts + shows
              // a toast WITHOUT polling. This is our IDLE-equivalent UX.
              try {
                window.dispatchEvent(new CustomEvent('mail-new', { detail: data }));
              } catch {}
              // Desktop notification — fires even when the user is on a
              // different tab. Only triggers if the user has explicitly
              // granted permission (we never auto-ask; that's handled by
              // a button in MailViewer). Silent no-op when unsupported
              // (older browsers) or when the tab is currently visible
              // (toast covers that case).
              try {
                if (
                  typeof Notification !== 'undefined' &&
                  Notification.permission === 'granted' &&
                  document.visibilityState !== 'visible'
                ) {
                  const top = (data.preview && data.preview[0]) || {};
                  const sender = top.from_name || top.from_addr || 'E-posta';
                  const title = data.new_count > 1
                    ? `${data.new_count} yeni e-posta · ${data.account_label || ''}`
                    : `Yeni e-posta · ${sender}`;
                  const body = (top.subject || '').slice(0, 120);
                  const n = new Notification(title, {
                    body,
                    icon: '/favicon.ico',
                    tag: `mail-${data.account_id}`,
                    renotify: true,
                  });
                  n.onclick = () => {
                    try { window.focus(); } catch {}
                    try { n.close(); } catch {}
                    // Best-effort deep-link; SPA routing handles the path.
                    if (window.location.pathname !== '/dashboard/mail') {
                      window.location.href = '/dashboard/mail';
                    }
                  };
                }
              } catch {}
              break;
            case 'pong':
              break;
            default:
              break;
          }
        } catch {}
      };

      ws.onclose = () => {
        if (!mountedRef.current) return;
        setConnected(false);
        if (pingRef.current) clearInterval(pingRef.current);
        // Auto-reconnect
        reconnectRef.current = setTimeout(() => {
          if (mountedRef.current) connect();
        }, WS_RECONNECT_DELAY);
      };

      ws.onerror = () => {
        // onclose will fire after onerror, reconnect handled there
      };
    } catch {
      reconnectRef.current = setTimeout(() => {
        if (mountedRef.current) connect();
      }, WS_RECONNECT_DELAY);
    }
  }, [accessToken, user?.id]);

  useEffect(() => {
    mountedRef.current = true;
    connect();

    return () => {
      mountedRef.current = false;
      if (pingRef.current) clearInterval(pingRef.current);
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null; // prevent reconnect on unmount
        wsRef.current.close();
      }
    };
  }, [connect]);

  // Send a JSON message through the WebSocket
  const sendWsMessage = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  return { onlineUsers, connected, sendWsMessage };
}
